import { USER_TYPE_LABELS } from "@anthra/platform/domain"
import { isSessionInvalid } from "@anthra/platform/errors"
import { ErrorState, Loading } from "@anthra/ui/feedback"
import { createFileRoute, Navigate, Outlet } from "@tanstack/react-router"

import { AppShell as Shell } from "@/components/app-shell"
import { useSession } from "@/features/auth/use-auth"
import { useSessionStore } from "@/stores/use-session-store"

/**
 * The authenticated shell — a pathless layout, so every screen under it is
 * guarded by one check rather than by each screen remembering to make it.
 *
 * THE GUARD RENDERS NOTHING UNTIL THE SESSION IS KNOWN. US-00-005.13 is explicit
 * that a banner must never cause an authenticated screen to render for an
 * unauthenticated person, and the same applies to a list or a thread: while the
 * session query is in flight this renders a loading state, and on a 401 it sends
 * the person to the entry screen. The 401 handler in ../lib/unauthenticated has
 * already held the destination by then, so they come back to where they were.
 *
 * THE STORE IS CHECKED BEFORE THE SESSION QUERY, and that order is the whole
 * point. Guarding on the session query alone only catches a session that was
 * already gone at page load, because that is the only case in which the query
 * itself 401s. When the session lapses MID-SESSION the 401 arrives on whatever
 * the person just did, while `useSession()` is sitting on a successful result
 * that is still fresh. The 401 handler empties the cache, but
 * `queryClient.clear()` DESTROYS query objects rather than refetching them: a
 * mounted observer keeps its last result and never asks again. So the session
 * query stays `success`, this guard keeps passing, and the person is left on an
 * authenticated screen reading an inline error that no navigation follows —
 * exactly what US-00-005.13 forbids. `status` is the only thing that observes the
 * 401 itself.
 */
export const Route = createFileRoute("/_app")({
	component: AppShell,
})

function AppShell() {
	const session = useSession()
	const sessionStatus = useSessionStore(state => state.status)

	// First, and ahead of the query: see the note above on a mid-session 401.
	if (sessionStatus === "signed_out") {
		return (
			<Navigate
				to="/entry"
				replace
			/>
		)
	}

	if (session.isPending) return <Loading label="Checking your session…" />

	// A DROPPED CONNECTION IS NOT A SIGN-OUT. This was
	// `if (session.isError || !session.data)` → <Navigate to="/entry" />, so
	// ANY failure of `GET /me` — the server being down, a flaky train tunnel, a
	// 500 — threw the person out to the sign-in screen. They had not been
	// signed out and their code still worked; the app simply could not tell the
	// difference between "your session ended" and "I could not ask".
	//
	// `isSessionInvalid` is the one that means signed out: a 401, or the
	// session-expired code. Everything else is a failure to reach the service,
	// and gets a retry rather than a sign-in form.
	if (session.isError && !isSessionInvalid(session.error)) {
		return (
			<div className="mx-auto w-full max-w-md px-4 py-16">
				<ErrorState
					error={session.error}
					onRetry={() => {
						void session.refetch()
					}}
				/>
			</div>
		)
	}

	if (session.isError || !session.data) {
		return (
			<Navigate
				to="/entry"
				replace
			/>
		)
	}

	return (
		<Shell
			fullName={session.data.fullName}
			userTypeLabel={USER_TYPE_LABELS[session.data.userType]}
		>
			<Outlet />
		</Shell>
	)
}
